import React, { useState, useEffect, useCallback, MouseEvent } from 'react';
import { reportsAPI, userModerationAPI, handleApiError } from '../services/api';
import { ProfileImage } from '../utils/imageUtils';
import './ReportAnalytics.css';

interface ReportedUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_image?: string;
  report_count: number;
  warning_count: number;
  is_banned?: boolean;
  is_suspended?: boolean;
}

interface UserBreakdown {
  breakdown: Record<string, number>;
}

const ReportAnalytics: React.FC = () => {
  const [mostReportedUsers, setMostReportedUsers] = useState<ReportedUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<ReportedUser | null>(null);
  const [userBreakdown, setUserBreakdown] = useState<UserBreakdown | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const loadMostReportedUsers = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportsAPI.getMostReportedUsers(50);
      setMostReportedUsers((response.data.users as unknown as ReportedUser[]) || []);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg?: string }> } } };
      const errorMessage = error.response?.data?.detail
        ? (Array.isArray(error.response.data.detail)
          ? error.response.data.detail.map((e) => e.msg || JSON.stringify(e)).join(', ')
          : error.response.data.detail)
        : handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMostReportedUsers();
  }, [loadMostReportedUsers]);

  const openUserModal = async (user: ReportedUser): Promise<void> => {
    setSelectedUser(user);
    setShowModal(true);

    // Load user reports breakdown
    try {
      const breakdown = await reportsAPI.getUserReportsBreakdown(user.id);
      const breakdownData = breakdown.data as unknown as { breakdown?: Record<string, number>; by_reason?: Record<string, number> };
      setUserBreakdown({ breakdown: breakdownData.breakdown || breakdownData.by_reason || {} });
    } catch (err) {
      console.error('Error loading user breakdown:', err);
    }
  };

  const closeModal = (): void => {
    setShowModal(false);
    setSelectedUser(null);
    setUserBreakdown(null);
  };

  // User Moderation Handlers
  const handleBanUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за баниране на ${userName}:`);
    if (reason !== null && reason.trim()) {
      try {
        await userModerationAPI.banUser(userId, reason.trim());
        alert('Потребителят е успешно банирван.');
        loadMostReportedUsers();
        closeModal();
      } catch (err) {
        alert('Грешка при банирането: ' + handleApiError(err));
      }
    }
  };

  const handleSuspendUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за временно спиране на ${userName}:`);
    if (reason !== null && reason.trim()) {
      const durationInput = prompt('Продължителност в часове (по подразбиране 72):', '72');
      const duration = parseInt(durationInput || '72') || 72;

      try {
        await userModerationAPI.suspendUser(userId, reason.trim(), duration);
        alert(`Потребителят е временно спрян за ${duration} часа.`);
        loadMostReportedUsers();
        closeModal();
      } catch (err) {
        alert('Грешка при спирането: ' + handleApiError(err));
      }
    }
  };

  const handleWarnUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за предупреждение на ${userName}:`);
    if (reason !== null && reason.trim()) {
      try {
        await userModerationAPI.warnUser(userId, reason.trim());
        alert('Предупреждението е изпратено.');
        loadMostReportedUsers();
        closeModal();
      } catch (err) {
        alert('Грешка при изпращането на предупреждение: ' + handleApiError(err));
      }
    }
  };

  const handleUnbanUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за премахване на бан от ${userName}:`);
    if (reason !== null && reason.trim()) {
      try {
        await userModerationAPI.unbanUser(userId, reason.trim());
        alert('Банът е премахнат.');
        loadMostReportedUsers();
        closeModal();
      } catch (err) {
        alert('Грешка при премахването на бан: ' + handleApiError(err));
      }
    }
  };

  const getStatusBadge = (user: ReportedUser): React.ReactNode => {
    if (user.is_banned) {
      return <span className="badge badge-danger">Банирван</span>;
    }
    if (user.is_suspended) {
      return <span className="badge badge-warning">Временно спрян</span>;
    }
    if (user.warning_count > 0) {
      return <span className="badge badge-info">{user.warning_count} предупреждения</span>;
    }
    return <span className="badge badge-success">Активен</span>;
  };

  const getReasonText = (reason: string): string => {
    switch (reason) {
      case 'spam': return 'Спам';
      case 'inappropriate': return 'Неподходящо поведение';
      case 'safety': return 'Проблем с безопасността';
      case 'other': return 'Друго';
      default: return reason;
    }
  };

  return (
    <div className="analytics-page">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Анализ на докладите</h2>
          <p className="last-updated">Най-докладвани потребители и статистики</p>
        </div>
        <button
          className={`floating-refresh ${loading ? 'loading' : ''}`}
          onClick={loadMostReportedUsers}
          disabled={loading}
          title="Обновяване на данните"
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ margin: '20px', padding: '15px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c66' }}>
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-chart-bar"></i>
            Най-докладвани потребители
          </h3>
          <div style={{ padding: '40px', textAlign: 'center', background: 'var(--glass-background)', borderRadius: '8px', border: '1px solid var(--glass-border)', backdropFilter: 'var(--glass-backdrop)' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: 'var(--color-text-secondary)' }}></i>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: '16px' }}>Зареждане на данни...</p>
          </div>
        </div>
      ) : mostReportedUsers.length === 0 ? (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-chart-bar"></i>
            Най-докладвани потребители
          </h3>
          <div style={{ padding: '40px', textAlign: 'center', background: 'var(--glass-background)', borderRadius: '8px', border: '1px solid var(--glass-border)', backdropFilter: 'var(--glass-backdrop)' }}>
            <i className="fas fa-check-circle" style={{ fontSize: '48px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}></i>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '16px', margin: '0' }}>
              Няма докладвани потребители
            </p>
          </div>
        </div>
      ) : (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-chart-bar"></i>
            Най-докладвани потребители ({mostReportedUsers.length})
          </h3>
          <div className="analytics-table-container">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Потребител</th>
                  <th>Брой доклади</th>
                  <th>Предупреждения</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {mostReportedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <ProfileImage
                          src={user.profile_image}
                          alt="Profile"
                          className="user-avatar-small"
                        />
                        <div>
                          <div className="user-name">{user.first_name} {user.last_name}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="report-count">{user.report_count}</span>
                    </td>
                    <td>
                      <span className="warning-count">{user.warning_count}</span>
                    </td>
                    <td>
                      {getStatusBadge(user)}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => openUserModal(user)}
                          title="Преглед на детайли"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleWarnUser(user.id, `${user.first_name} ${user.last_name}`)}
                          title="Предупреждение"
                        >
                          <i className="fas fa-exclamation-triangle"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleSuspendUser(user.id, `${user.first_name} ${user.last_name}`)}
                          title="Временно спиране"
                        >
                          <i className="fas fa-clock"></i>
                        </button>
                        {!user.is_banned ? (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleBanUser(user.id, `${user.first_name} ${user.last_name}`)}
                            title="Банирай"
                            style={{ backgroundColor: '#dc2626' }}
                          >
                            <i className="fas fa-ban"></i>
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleUnbanUser(user.id, `${user.first_name} ${user.last_name}`)}
                            title="Премахни бан"
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e: MouseEvent) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Детайли за потребител</h3>
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="user-detail-section">
                <h4>Информация за потребител</h4>
                <div className="user-info-detailed">
                  <ProfileImage
                    src={selectedUser.profile_image}
                    alt="Profile"
                    className="user-avatar-large"
                  />
                  <div>
                    <p><strong>Име:</strong> {selectedUser.first_name} {selectedUser.last_name}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Общо доклади:</strong> {selectedUser.report_count}</p>
                    <p><strong>Предупреждения:</strong> {selectedUser.warning_count}</p>
                    <p><strong>Статус:</strong> {getStatusBadge(selectedUser)}</p>
                  </div>
                </div>
              </div>

              {userBreakdown && (
                <div className="user-detail-section">
                  <h4>Разпределение на докладите</h4>
                  {Object.keys(userBreakdown.breakdown).map(reason => (
                    <div key={reason} className="breakdown-item">
                      <span className="breakdown-reason">{getReasonText(reason)}:</span>
                      <span className="breakdown-count">{userBreakdown.breakdown[reason]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-warning"
                onClick={() => handleWarnUser(selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`)}
              >
                <i className="fas fa-exclamation-triangle"></i> Предупреди
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleSuspendUser(selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`)}
                style={{ backgroundColor: '#f97316' }}
              >
                <i className="fas fa-clock"></i> Спри
              </button>
              {!selectedUser.is_banned ? (
                <button
                  className="btn btn-danger"
                  onClick={() => handleBanUser(selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`)}
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <i className="fas fa-ban"></i> Банирай
                </button>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={() => handleUnbanUser(selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`)}
                >
                  <i className="fas fa-check"></i> Премахни бан
                </button>
              )}
              <button className="btn btn-default" onClick={closeModal}>
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportAnalytics;
